/**
 * FAQStats Component
 *
 * Displays FAQ statistics cards for moderators
 * Extracted from FAQs.tsx as part of Phase 3 decomposition
 *
 * Responsibilities:
 * - Display 4 statistic cards (pending, approved, rejected, total)
 * - Handle loading state with skeletons
 * - Color-coded icons for each stat type
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface FAQStatsProps {
  /** FAQ statistics data */
  stats: {
    pending_review: number;
    approved_faqs: number;
    rejected_faqs: number;
    total_faqs: number;
  } | undefined;
  /** Whether stats are currently loading */
  isLoading: boolean;
  /**
   * Called when a card is clicked. A card shows a number, so the reader expects
   * to reach exactly that set. Omit the handler and the cards stay purely
   * informational, which is what a user without moderation rights sees.
   */
  onSelect?: (filter: FAQStatsFilter) => void;
  /** Which card the list below currently follows; it gets marked. */
  activeFilter?: FAQStatsFilter;
}

/** Which set of FAQs a card stands for. */
export type FAQStatsFilter = 'pending_review' | 'approved' | 'rejected' | 'all';

/**
 * Memoized Stat Card Component
 */
const StatCard = React.memo<{
  icon: React.ReactNode;
  value: number;
  label: string;
  color: 'warning' | 'success' | 'error' | 'primary';
  isLoading: boolean;
  onClick?: () => void;
  active?: boolean;
  urgent?: boolean;
}>(({ icon, value, label, color, isLoading, onClick, active = false, urgent = false }) => {
  const clickable = Boolean(onClick);
  return (
    <Card
      onClick={onClick}
      // Keyboard reachable only when it actually does something; a card that
      // just shows a number must not collect focus.
      {...(clickable
        ? {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            },
          }
        : {})}
      sx={{
        height: '100%',
        // The active card carries the filter, so it has to look like it does:
        // a coloured border and a tinted ground. Without this the only clue is
        // the banner below, and the card that caused it looks untouched.
        // Wartet etwas auf Pruefung, muss die Kachel das zeigen, auch wenn sie
        // nicht die gewaehlte ist - sonst uebersieht man offene Arbeit. Die
        // aktive Kachel gewinnt, damit nicht zwei um dieselbe Aufmerksamkeit
        // konkurrieren.
        ...(urgent && !active && {
          borderColor: `${color}.main`,
          bgcolor: `${color}.lighter`,
        }),
        ...(active && {
          borderColor: `${color}.main`,
          borderWidth: 2,
          bgcolor: `${color}.lighter`,
          boxShadow: 2,
        }),
        ...(clickable && {
          cursor: 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': {
            borderColor: `${color}.main`,
            boxShadow: 3,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: `${color}.main`,
            outlineOffset: 2,
          },
        }),
      }}
    >
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography
            variant="h4"
            color={`${color}.main`}
            sx={urgent ? { fontWeight: 700 } : undefined}
          >
            {isLoading ? <Skeleton width={60} /> : value}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
});

StatCard.displayName = 'StatCard';

/**
 * FAQStats - Display FAQ statistics for moderators
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 * Each stat card is also memoized individually
 *
 * @example
 * ```tsx
 * <FAQStats
 *   stats={{
 *     pending_review: 5,
 *     approved_faqs: 42,
 *     rejected_faqs: 3,
 *     total_faqs: 50
 *   }}
 *   isLoading={false}
 * />
 * ```
 */
export const FAQStats = React.memo<FAQStatsProps>(({ stats, isLoading, onSelect, activeFilter }) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* Pending Review */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<PendingIcon color="warning" />}
          value={stats?.pending_review || 0}
          label={t('faq.moderation.stats.pending_review')}
          color="warning"
          isLoading={isLoading}
          {...(onSelect ? { onClick: () => onSelect('pending_review') } : {})}
          urgent={(stats?.pending_review ?? 0) > 0}
          active={activeFilter === 'pending_review'}
        />
      </Grid>

      {/* Approved */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<ApprovedIcon color="success" />}
          value={stats?.approved_faqs || 0}
          label={t('faq.moderation.stats.approved')}
          color="success"
          isLoading={isLoading}
          {...(onSelect ? { onClick: () => onSelect('approved') } : {})}
          active={activeFilter === 'approved'}
        />
      </Grid>

      {/* Rejected */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<RejectedIcon color="error" />}
          value={stats?.rejected_faqs || 0}
          label={t('faq.moderation.stats.rejected')}
          color="error"
          isLoading={isLoading}
          {...(onSelect ? { onClick: () => onSelect('rejected') } : {})}
          active={activeFilter === 'rejected'}
        />
      </Grid>

      {/* Total */}
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard
          icon={<CategoryIcon color="primary" />}
          value={stats?.total_faqs || 0}
          label={t('faq.stats.totalFaqs')}
          color="primary"
          isLoading={isLoading}
          {...(onSelect ? { onClick: () => onSelect('all') } : {})}
          active={activeFilter === 'all'}
        />
      </Grid>
    </Grid>
  );
});

FAQStats.displayName = 'FAQStats';
